import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, CloudUpload, InsertDriveFile } from '@mui/icons-material';
import { useUploadImportFileMutation } from '../store/hooks';

interface DataMigrationUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_FILE_TYPES = ['.zip', '.xml', '.csv'];
const ALLOWED_MIME_TYPES = ['application/zip', 'application/x-zip-compressed', 'text/xml', 'application/xml', 'text/csv', 'application/csv'];

export function DataMigrationUploadModal({
  open,
  onClose,
  onSuccess,
  organizationId,
}: DataMigrationUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadFile, { isLoading }] = useUploadImportFileMutation();

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 100MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
      return 'Invalid file type. Only ZIP, XML, and CSV files are allowed.';
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type) && file.type !== '') {
      // Sometimes MIME type is empty for certain files, so we rely on extension
      if (!file.name.toLowerCase().endsWith('.zip') && !file.name.toLowerCase().endsWith('.xml') && !file.name.toLowerCase().endsWith('.csv')) {
        return 'Invalid file type. Only ZIP, XML, and CSV files are allowed.';
      }
    }

    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setUploadProgress(0);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleUpload = async () => {
    if (!selectedFile || !organizationId) return;

    // Validate UUID format for organization_id
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(organizationId)) {
      console.error('Invalid organization ID format (not a valid UUID):', organizationId);
      setError(`Invalid organization ID format: ${organizationId}. Expected UUID format.`);
      return;
    }

    try {
      setUploadProgress(25);

      const result = await uploadFile({
        file: selectedFile,
        organizationId: organizationId
      }).unwrap();

      setUploadProgress(100);

      // Success
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 500);
    } catch (error: any) {
      // Better error handling - check different error formats per ImportErrorResponse schema
      let errorMessage = 'Failed to upload file. Please try again.';

      // Primary check for 'error' field as per ImportErrorResponse schema
      if (error?.data?.error) {
        errorMessage = error.data.error;
        // Check if there are additional details
        if (error?.data?.details?.message) {
          errorMessage += `: ${error.data.details.message}`;
        }
      } else if (error?.message) {
        // This is the message we set in our queryFn
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.detail) {
        errorMessage = error.data.detail;
      } else if (typeof error?.data === 'string') {
        errorMessage = error.data;
      } else if (error?.status === 400) {
        errorMessage = 'Invalid request. Please check the file format and try again.';
      } else if (error?.status === 404) {
        errorMessage = 'Upload endpoint not found. Please contact support.';
      } else if (error?.status === 413) {
        errorMessage = 'File is too large. Maximum file size is 100MB.';
      } else if (error?.status === 422) {
        errorMessage = 'Invalid data format. Please ensure the file is a valid ZIP, XML, or CSV file.';
      }

      setError(errorMessage);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    setIsDragging(false);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Upload Patient Data for Review</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragging ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1976d2',
                bgcolor: 'rgba(25, 118, 210, 0.02)',
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,.xml,.csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <>
                <InsertDriveFile sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
                {uploadProgress > 0 && (
                  <Box sx={{ mt: 2, width: '100%' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag & Drop or Click to Upload for Review
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload ZIP, XML, or CSV files (up to 100MB) to review before importing
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload Process:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Accepted formats: ZIP, XML, and CSV files (max 100MB)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Data type: Patient records and demographics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Processing: Files are parsed and prepared for review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Next step: Review data and decide to import, merge, or discard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              Note: Patient matching and deduplication features coming soon
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || isLoading}
          startIcon={isLoading ? undefined : <CloudUpload />}
        >
          {isLoading ? 'Uploading...' : 'Upload for Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}